/**
 * Portal copy explaining why a role cannot be checked.
 * The rules themselves (which roles cover or conflict with which) live in skipify-types.
 */
import {
  AuthRoles,
  findRolesConflicting,
  findRolesCovering,
  normalizePortalRoleNames,
} from 'skipify-types';

import { getPortalUserRoleLabel } from './portalUserRoleCatalog';

export { normalizePortalRoleNames };

const PAYLINK_ROLE_NAMES = new Set<string>([
  AuthRoles.PAYLINK_BASIC_USER,
  AuthRoles.PAYLINK_STANDARD_USER,
  AuthRoles.PAYLINK_SUPER_USER,
]);

const MIT_ROLE_NAMES = new Set<string>([AuthRoles.MIT_SUPER_USER, AuthRoles.MIT_STANDARD_USER]);

const joinRoleLabels = (roleNames: string[]) =>
  roleNames.map((roleName) => getPortalUserRoleLabel(roleName)).join(' or ');

const formatCoverageHelperText = (blockedRoleName: string, coveringRoleNames: string[]): string => {
  const coveringLabels = joinRoleLabels(coveringRoleNames);

  if (
    PAYLINK_ROLE_NAMES.has(blockedRoleName) &&
    coveringRoleNames.some((name) => MIT_ROLE_NAMES.has(name))
  ) {
    return `Paylink permissions are already included in ${coveringLabels}.`;
  }

  return `Permissions for this role are already granted by ${coveringLabels}.`;
};

/**
 * Helper text for a role checkbox, or undefined when the role can be assigned.
 * Coverage is checked before conflicts so roles subsumed by a broader selection (e.g. Paylink
 * when Card on file full access is selected) explain inclusion rather than incompatibility.
 */
export const getRoleRedundancyHelperText = (
  roleName: string,
  selectedRoleNames: string[],
): string | undefined => {
  const coveringRoles = findRolesCovering(roleName, selectedRoleNames);
  if (coveringRoles.length > 0) {
    return formatCoverageHelperText(roleName, coveringRoles);
  }

  const conflictingRoles = findRolesConflicting(roleName, selectedRoleNames);
  if (conflictingRoles.length > 0) {
    if (PAYLINK_ROLE_NAMES.has(roleName)) {
      const selectedMitRoles = conflictingRoles.filter((name) => MIT_ROLE_NAMES.has(name));
      if (selectedMitRoles.length > 0) {
        return `Paylink permissions are already included in ${joinRoleLabels(selectedMitRoles)}.`;
      }
    }

    if (MIT_ROLE_NAMES.has(roleName)) {
      const selectedPaylinkRoles = conflictingRoles.filter((name) => PAYLINK_ROLE_NAMES.has(name));
      if (selectedPaylinkRoles.length > 0) {
        const paylinkLabels = joinRoleLabels(selectedPaylinkRoles);
        return `Paylink is enough for payment links only. Uncheck ${paylinkLabels} to assign Card on file if they need saved-card access.`;
      }
    }

    return `Not compatible with ${joinRoleLabels(conflictingRoles)}.`;
  }

  return undefined;
};
